/**
 * FaccetteEditor — gestione delle FACCETTE (le dimensioni della tassonomia).
 *
 * Le faccette sono dati, non codice: aggiungerne una non richiede un deploy,
 * perché il prompt dell'AI viene costruito a runtime da questa collezione.
 * `descrizione_ai` è il testo con cui la faccetta viene spiegata al modello:
 * scriverla bene conta più di qualunque messa a punto del codice.
 */

import React, { useState } from 'react';
import {
  Box, Button, FormControlLabel, IconButton, MenuItem, Paper, Stack, Switch,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Edit, Save } from '@mui/icons-material';
import { tokens } from '../../theme';
import { COLORE_FACCETTA_DEFAULT } from './labels';
import { saveFaccetta } from '../../services/reteContattiService';
import type { FaccettaRete, FaccettaReteWriteInput } from '../../types/reteContatti';

/**
 * Tavolozza delle faccette: SOLO colori già nel tema della console (le "tessere"
 * tier + gli stati). Niente colori inventati, così la pagina parla la stessa
 * lingua del resto della piattaforma.
 */
const COLORI: Array<{ valore: string; nome: string }> = [
  { valore: tokens.color.tierBusiness.main, nome: 'viola' },
  { valore: tokens.color.tierPro.main, nome: 'cobalto' },
  { valore: tokens.color.tierFree.main, nome: 'slate' },
  { valore: tokens.color.tierCustom.main, nome: 'ambra' },
  { valore: tokens.color.success, nome: 'verde' },
  { valore: tokens.color.info, nome: 'azzurro' },
];

interface Props {
  faccette: FaccettaRete[];
  onCambiata: () => Promise<void>;
  showSnack: (msg: string, severity: 'success' | 'error') => void;
}

const VUOTA: FaccettaReteWriteInput = {
  etichetta: '',
  descrizione_ai: '',
  ordine: 0,
  colore: COLORI[0].valore,
  consenti_proposte_ai: true,
};

export const FaccetteEditor: React.FC<Props> = ({ faccette, onCambiata, showSnack }) => {
  /** id in modifica, oppure 'nuova', oppure null (chiuso). */
  const [modifica, setModifica] = useState<string | null>(null);
  const [form, setForm] = useState<FaccettaReteWriteInput>(VUOTA);
  const [salvando, setSalvando] = useState(false);

  const apriNuova = () => {
    setForm({ ...VUOTA, ordine: (faccette.at(-1)?.ordine ?? 0) + 10 });
    setModifica('nuova');
  };

  const apriModifica = (f: FaccettaRete) => {
    setForm({
      etichetta: f.etichetta,
      descrizione_ai: f.descrizione_ai,
      ordine: f.ordine ?? 0,
      colore: f.colore || COLORE_FACCETTA_DEFAULT,
      consenti_proposte_ai: f.consenti_proposte_ai !== false,
    });
    setModifica(f.id);
  };

  const salva = async () => {
    if (!form.etichetta.trim() || !form.descrizione_ai.trim()) {
      showSnack('Servono etichetta e descrizione per l’AI', 'error');
      return;
    }
    setSalvando(true);
    try {
      // Con un id esistente si RINOMINA (lo slug resta: lo referenziano i tag).
      await saveFaccetta(form, modifica === 'nuova' ? undefined : modifica ?? undefined);
      setModifica(null);
      await onCambiata();
      showSnack('Faccetta salvata', 'success');
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Salvataggio non riuscito', 'error');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Box>
      <Stack spacing={0.75} sx={{ mb: 1.5 }}>
        {faccette.map((f) => (
          <Stack key={f.id} direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 10, height: 10, borderRadius: '50%',
                bgcolor: f.colore || COLORE_FACCETTA_DEFAULT, flexShrink: 0,
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.graphite }}>
              {f.etichetta}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: tokens.color.mist, flex: 1, minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {f.descrizione_ai}
            </Typography>
            <Tooltip title="Modifica">
              <IconButton size="small" onClick={() => apriModifica(f)}>
                <Edit sx={{ fontSize: 15, color: tokens.color.mist }} />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Stack>

      {modifica === null ? (
        <Button
          size="small" startIcon={<Add sx={{ fontSize: 16 }} />} onClick={apriNuova}
          sx={{ textTransform: 'none' }}
        >
          nuova faccetta
        </Button>
      ) : (
        <Paper variant="outlined" sx={{ p: 1.5, borderColor: tokens.color.line }}>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Etichetta" size="small" fullWidth value={form.etichetta}
                onChange={(e) => setForm((p) => ({ ...p, etichetta: e.target.value }))}
              />
              <TextField
                label="Colore" select size="small" value={form.colore}
                onChange={(e) => setForm((p) => ({ ...p, colore: e.target.value }))}
                sx={{ minWidth: 130 }}
              >
                {COLORI.map((c) => (
                  <MenuItem key={c.valore} value={c.valore}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: c.valore }} />
                      <span>{c.nome}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Ordine" type="number" size="small" value={form.ordine}
                onChange={(e) => setForm((p) => ({ ...p, ordine: Number(e.target.value) || 0 }))}
                sx={{ width: 100 }}
              />
            </Stack>
            <TextField
              label="Come spiegarla all'AI"
              size="small" fullWidth multiline minRows={2}
              value={form.descrizione_ai}
              onChange={(e) => setForm((p) => ({ ...p, descrizione_ai: e.target.value }))}
              helperText="Finisce nel prompt: dì che tipo di informazione va in questa dimensione."
            />
            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={form.consenti_proposte_ai}
                  onChange={(e) => setForm((p) => ({ ...p, consenti_proposte_ai: e.target.checked }))}
                />
              )}
              label={(
                <Typography variant="body2" sx={{ color: tokens.color.ash }}>
                  l'AI può proporre tag nuovi in questa faccetta
                </Typography>
              )}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained" size="small" startIcon={<Save sx={{ fontSize: 16 }} />}
                onClick={salva} disabled={salvando}
                sx={{ textTransform: 'none' }}
              >
                salva
              </Button>
              <Button
                size="small" color="inherit" onClick={() => setModifica(null)}
                disabled={salvando} sx={{ textTransform: 'none' }}
              >
                annulla
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};
